import { useAsync } from 'react-use';

import { logger } from '@service-initiation/shared-clients-osp-logger';
import {
  bookingWindowIdAtom,
  selectedAppointmentAsyncAtom,
  selectedDeviceTypeAsyncAtom,
  selectedRepairsAsyncAtom,
  store,
  useCustomerDetailsAtom,
  useRemoteTechValue,
  useSelectedStoreAsyncAtom,
} from '@service-initiation/tech-care-data-access-store';
import {
  ErrorType,
  getBookingWindows,
  getDeviceTypeOptions,
} from '@service-initiation/tech-care-data-access-tech-care-services';
import {
  convertAppointmentFormat,
  getHyphenatedDate,
} from '@service-initiation/tech-care-utils-date-time';

export const useConfirmWindowAvailability = () => {
  const [customerDetails] = useCustomerDetailsAtom();

  const remoteTech = useRemoteTechValue();
  const { value: selectedStore } = useSelectedStoreAsyncAtom();

  return useAsync(async () => {
    const deviceType = await store.get(selectedDeviceTypeAsyncAtom);
    const selectedRepairs = await store.get(selectedRepairsAsyncAtom);
    const appointment = await store.get(selectedAppointmentAsyncAtom);
    const appointmentDate = appointment?.date;
    const appointmentEndTime = appointment?.endTime;
    const appointmentIntentId = appointment?.intentId;
    const appointmentStartTime = appointment?.startTime;

    if (
      !selectedRepairs?.[0].id ||
      !remoteTech?.meetingAddress?.zip ||
      !appointmentDate ||
      !appointmentStartTime
    ) {
      const err = new Error(
        'Missing necessary user state when trying to validate time slot.'
      );
      logger.error(err, {
        data: {
          appointmentInfo: {
            selectedAppointmentDate: appointmentDate,
            selectedAppointmentTime: appointmentStartTime,
            storeTimezone: selectedStore?.time_zone,
          },
          customerDetails: {
            isEmail: !!customerDetails?.email,
            isFirstName: !!customerDetails?.firstName,
            isLastName: !!customerDetails?.lastName,
            isPhoneNumber: !!customerDetails?.phoneNumber,
          },
          deviceTypeId: deviceType?.id,
          intentId: appointmentIntentId,
          selectedRepairIds: selectedRepairs?.map((repair) => repair.id),
          storeId: selectedStore?.id,
        },
      });
      throw err;
    }

    const bookingWindowsDate = getHyphenatedDate(appointmentDate);
    const { window_end, window_start } = convertAppointmentFormat({
      date: appointmentDate,
      endTime: appointmentEndTime,
      startTime: appointmentStartTime,
    });

    const deviceTypeOptions = (
      await getDeviceTypeOptions({
        deviceRepairId: selectedRepairs?.[0].id,
      })
    ).option_types.reduce(
      (acc, { options = [] }): number[] => [
        ...acc,
        ...options.reduce(
          (acc, { option_ids = [] }): number[] => [...acc, ...option_ids],
          [] as number[]
        ),
      ],
      [] as number[]
    );

    const bookingWindows = await getBookingWindows({
      partner_data: {
        device_repair_id: selectedRepairs?.[0].id,
        device_type_option_ids: deviceTypeOptions,
      },
      postal_code: remoteTech?.meetingAddress?.zip,
      quote_range_end: bookingWindowsDate,
      quote_range_start: bookingWindowsDate,
    });

    const matchedBookingWindow = bookingWindows.available_windows.find(
      (bookingWindow) => {
        return (
          bookingWindow.window_start === window_start &&
          bookingWindow.window_end === window_end
        );
      }
    );

    if (matchedBookingWindow) {
      store.set(bookingWindowIdAtom, matchedBookingWindow.window_id);
      const selectedAppointment = await store.get(selectedAppointmentAsyncAtom);

      if (!selectedAppointment.date) {
        throw new Error('Attempting to set appointment without a date');
      }
      store.set(selectedAppointmentAsyncAtom, {
        date: selectedAppointment.date,
        endTime: selectedAppointment.endTime,
        startTime: selectedAppointment.startTime,
      });
    } else {
      throw new Error(ErrorType.SlotIsTakenError);
    }
  }, [
    remoteTech?.meetingAddress?.zip,
    customerDetails?.email,
    customerDetails?.firstName,
    customerDetails?.lastName,
    customerDetails?.phoneNumber,
    selectedStore?.id,
    selectedStore?.time_zone,
  ]);
};
